import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, BarChart3, FileText, Send, Eye, CalendarClock, HeartHandshake, AlertTriangle, Gauge } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { KpiStatCard } from "@/components/KpiStatCard";
import { TrendChart } from "@/components/TrendChart";
import { EmptyState } from "@/components/EmptyState";
import { SimulatedBanner } from "@/components/SimulatedBanner";
import { OnboardingCard } from "@/components/OnboardingCard";
import { ReplyInsightsCard } from "@/components/ReplyInsightsCard";
import { ReadinessCard } from "@/components/ReadinessCard";
import { TrendingDeckBanner } from "@/components/TrendingDeckBanner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { METRIC_DEFS, computeDelta } from "@/lib/metrics";
import { formatPeriodLabel, formatDate } from "@/lib/format";

export default function Dashboard() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [cadence, setCadence] = useState(null);
  const [sweat, setSweat] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company) return;
    (async () => {
      setLoading(true);
      try {
        const [m, u, cfg, cad, se, al, bm] = await Promise.all([
          api.get(`/companies/${company.id}/metrics`),
          api.get(`/companies/${company.id}/updates`),
          api.get(`/config`),
          api.get(`/companies/${company.id}/cadence-status`),
          api.get(`/companies/${company.id}/sweat-equity`).catch(() => ({ data: { summary: {} } })),
          api.get(`/companies/${company.id}/metric-alerts`).catch(() => ({ data: { alerts: [] } })),
          api.get(`/companies/${company.id}/kpi-benchmarks`).catch(() => ({ data: null })),
        ]);
        setMetrics(m.data);
        setUpdates(u.data);
        setEmailEnabled(cfg.data.email_enabled);
        setCadence(cad.data);
        setSweat(se.data.summary);
        setAlerts(al.data.alerts || []);
        setBench(bm.data);
      } finally { setLoading(false); }
    })();
  }, [company]);

  const latest = metrics[metrics.length - 1];
  const prev = metrics[metrics.length - 2];
  const seriesFor = (key) => metrics.map((m) => m[key]);

  const CTA = (
    <Button onClick={() => navigate("/app/updates/new")} className="gap-2" data-testid="dashboard-new-update">
      <Plus className="h-4 w-4" /> New Update
    </Button>
  );

  return (
    <AppShell title="Overview" subtitle={company?.name} actions={CTA}>
      {!emailEnabled && <div className="mb-6"><SimulatedBanner /></div>}

      <OnboardingCard />

      <TrendingDeckBanner />

      {alerts.length > 0 && (
        <Card className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="metric-alerts">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Metric alerts</div>
          <div className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`metric-alert-${a.metric}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-500" : "bg-slate-400"}`} />
                <span className="text-amber-950">{a.message}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {cadence?.is_due && (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="cadence-reminder">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"><CalendarClock className="h-5 w-5" /></div>
            <div>
              <div className="text-sm font-semibold text-foreground">Your {cadence.cadence} update for {formatPeriodLabel(cadence.current_period)} is due</div>
              <div className="text-xs text-muted-foreground">{cadence.last_published_period ? `Last published: ${formatPeriodLabel(cadence.last_published_period)}` : "You haven't published an update yet."}</div>
            </div>
          </div>
          <Button onClick={() => navigate("/app/updates/new")} className="gap-2" data-testid="cadence-create">
            <Plus className="h-4 w-4" /> Create {formatPeriodLabel(cadence.current_period)} update
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" data-testid="loading-skeleton" />)}
        </div>
      ) : metrics.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No metrics yet"
          description="Your health dashboard, runway, and AI drafts start from one month of numbers."
          steps={["Add this month's revenue, cash, and burn (manually or by CSV)", "Add your first investor so updates have somewhere to go", "Draft your first update; TenX writes from the numbers you entered"]}
          actionLabel="Add metrics"
          onAction={() => navigate("/app/metrics")}
          secondaryLabel="Add investors"
          secondaryTo="/app/investors"
          testId="empty-state-metrics"
        />
      ) : (
        <>
          {sweat?.total_accrued > 0 && (
            <button
              onClick={() => navigate("/app/sweat-equity")}
              className="mb-6 flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border bg-primary/5 p-4 text-left transition-colors hover:border-primary/40"
              data-testid="dashboard-sweat-tile"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"><HeartHandshake className="h-5 w-5" /></div>
                <div>
                  <div className="text-sm font-semibold text-foreground">Founder sweat equity invested</div>
                  <div className="text-xs text-muted-foreground">{sweat.people} contributor{sweat.people === 1 ? "" : "s"} · {sweat.runway_months_saved} months of runway saved</div>
                </div>
              </div>
              <div className="text-right">
                <div className="h-display text-2xl font-semibold tabular-nums text-primary">{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(sweat.total_accrued)}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">to date</div>
              </div>
            </button>
          )}
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">Health snapshot · {formatPeriodLabel(latest?.period)}</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {METRIC_DEFS.map((def) => (
              <KpiStatCard
                key={def.key}
                def={def}
                value={latest?.[def.key]}
                delta={prev ? computeDelta(latest?.[def.key], prev?.[def.key]) : null}
                series={seriesFor(def.key)}
              />
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="rounded-xl border bg-card p-5">
              <div className="mb-3 text-sm font-semibold text-foreground">MRR trend</div>
              <TrendChart data={metrics} dataKey="mrr" type="currency" />
            </Card>
            <Card className="rounded-xl border bg-card p-5">
              <div className="mb-3 text-sm font-semibold text-foreground">Net burn trend</div>
              <TrendChart data={metrics} dataKey="net_burn" type="currency" color="hsl(var(--chart-3))" />
            </Card>
          </div>

          {bench?.benchmarks?.some((b) => b.value != null) && (
            <Card className="mt-8 rounded-xl border bg-card p-5" data-testid="kpi-benchmarks">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><Gauge className="h-4 w-4 text-primary" /> vs {bench.stage} B2B SaaS benchmarks</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {bench.benchmarks.map((b) => (
                  <div key={b.key} className="rounded-lg bg-secondary/60 p-3" data-testid={`benchmark-${b.key}`}>
                    <div className="text-[11px] text-muted-foreground">{b.label}</div>
                    <div className="mt-0.5 flex items-baseline gap-2">
                      <span className="h-display text-lg font-semibold tabular-nums">{b.value ?? "-"}</span>
                      <span className="text-[11px] text-muted-foreground">median {b.median} · top {b.top_quartile}</span>
                    </div>
                    {b.status && (
                      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        b.status === "top_quartile" ? "bg-emerald-50 text-emerald-700" :
                          b.status === "above_median" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                        {b.status === "top_quartile" ? "Top quartile" : b.status === "above_median" ? "Above median" : "Below median"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="h-display text-lg font-semibold text-foreground">Recent updates</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/updates")} data-testid="dashboard-view-all-updates">View all</Button>
        </div>
        {updates.length === 0 ? (
          <EmptyState icon={FileText} title="No updates yet" description="Draft your first investor update with AI assistance." actionLabel="Create update" onAction={() => navigate("/app/updates/new")} testId="empty-state-updates" />
        ) : (
          <div className="space-y-2">
            {updates.slice(0, 5).map((u) => (
              <Card key={u.id} className="flex items-center justify-between rounded-xl border bg-card p-4 transition-shadow hover:shadow-[var(--shadow-md)] cursor-pointer" onClick={() => navigate(`/app/updates/${u.id}/edit`)} data-testid={`update-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{u.title}</div>
                  <div className="text-xs text-muted-foreground">{formatPeriodLabel(u.period)} · {formatDate(u.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  {u.status === "published" ? (
                    <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700"><Send className="mr-1 h-3 w-3" />Published</Badge>
                  ) : (
                    <Badge className="border-slate-200 bg-slate-50 text-slate-700">Draft</Badge>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-6 lg:col-span-2">
        <ReadinessCard />
        <ReplyInsightsCard />
      </div>
      </div>
    </AppShell>
  );
}
